import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { ThemeProvider } from '@emotion/react';
import { MuiTheme } from '../styles/MuiDataTableStyle';
import Colors from '../theme/Colors';
import {
  getDelinquentPartners,
  downloadDelinquentPartnersExcel,
} from '../services/delinquentPartnersService';
import { useStateAuthValue } from '../context/AuthState';
import { ToastType, showToast } from '../utils/ToastUtil';
import { useToast } from '../context/Toast/ToastProvider';
import PageHeader from '../components/PageHeader';

// Columnas fijas según el esquema real de /delinquent-partners ({ totalItems, partners: [...] }).
const COLUMNS = [
  { key: 'apellido', label: 'Apellido' },
  { key: 'nombre', label: 'Nombre' },
  { key: 'dni', label: 'DNI' },
  { key: 'email', label: 'Email' },
  { key: 'qxCna', label: 'Cuotas No Aprob.' },
  { key: 'activated', label: 'Activado' },
  { key: 'paused', label: 'Pausado' },
  { key: 'plan', label: 'Plan' },
  { key: 'estado', label: 'Estado' },
  // { key: 'provincia', label: 'Provincia' },
  // { key: 'canalDeVentas', label: 'Canal de ventas' },
  // { key: 'empresa', label: 'Empresa' },
  // { key: 'formaDePago', label: 'Forma de pago' },
  // { key: 'fechaActivacion', label: 'Fecha activación' },
  // { key: 'fechaBaja', label: 'Fecha baja' },
];

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Si' : 'No';
  if (key === 'fechaActivacion' || key === 'fechaBaja') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('es-AR');
  }
  return String(value);
};

const getRowKey = (row, index) => row.subscriptionId ?? row.id ?? row.usrId ?? row.dni ?? index;

export default function DelinquentPartners() {
  const [{ userToken }] = useStateAuthValue();
  const [qxCna, setQxCna] = useState('');
  const [qxCnaInput, setQxCnaInput] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [allRowsSelected, setAllRowsSelected] = useState(false);
  const [excludedRowKeys, setExcludedRowKeys] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const toastDispatch = useToast();
  const displayError = (msg) =>
    showToast(toastDispatch, msg, ToastType.ERROR, ToastType.ERROR);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDelinquentPartners(
        userToken,
        { qxCna, page, size: rowsPerPage },
        displayError
      );
      const partners = response?.partners ?? (Array.isArray(response) ? response : []);
      setRows(partners);
      setTotalElements(response?.totalItems ?? partners.length);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken, qxCna, page, rowsPerPage]);

  useEffect(() => {
    if (hasSearched) {
      fetchData();
    } else {
      setRows([]);
      setSelectedRowKeys([]);
      setAllRowsSelected(false);
      setExcludedRowKeys([]);
      setTotalElements(0);
    }
  }, [fetchData, hasSearched]);

  // Debounce del filtro por qxCna antes de disparar la búsqueda.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(0);
      setSelectedRowKeys([]);
      setAllRowsSelected(false);
      setExcludedRowKeys([]);
      setQxCna(qxCnaInput);
      setHasSearched(Boolean(qxCnaInput));
    }, 400);
    return () => clearTimeout(timeout);
  }, [qxCnaInput]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      await downloadDelinquentPartnersExcel(userToken, { qxCna }, displayError);
    } finally {
      setDownloading(false);
    }
  };

  const handleToggleRow = (row, index) => {
    const rowKey = getRowKey(row, index);

    if (allRowsSelected) {
      setExcludedRowKeys((currentKeys) =>
        currentKeys.includes(rowKey)
          ? currentKeys.filter((key) => key !== rowKey)
          : [...currentKeys, rowKey]
      );
      return;
    }

    setSelectedRowKeys((currentKeys) =>
      currentKeys.includes(rowKey)
        ? currentKeys.filter((key) => key !== rowKey)
        : [...currentKeys, rowKey]
    );
  };

  const handleToggleAllRows = () => {
    if (allRowsSelected) {
      setAllRowsSelected(false);
      setExcludedRowKeys([]);
      setSelectedRowKeys([]);
    } else {
      setAllRowsSelected(true);
      setExcludedRowKeys([]);
      setSelectedRowKeys([]);
    }
  };

  const selectedCount = allRowsSelected
    ? Math.max(totalElements - excludedRowKeys.length, 0)
    : selectedRowKeys.length;
  const selectedRowsOnPage = rows.filter((row, index) => {
    const rowKey = getRowKey(row, index);
    return allRowsSelected
      ? !excludedRowKeys.includes(rowKey)
      : selectedRowKeys.includes(rowKey);
  });

  const downloadButton = (
    <Tooltip title="Descargar archivo Excel">
      <span>
        <IconButton
          disabled={downloading}
          onClick={handleDownloadExcel}
          sx={{
            color: 'rgba(0, 0, 0, 0.54)',
            '&:hover': { color: Colors.AzulMediline },
          }}
        >
          {downloading ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <ThemeProvider theme={MuiTheme}>
      <div className="users">
        <div className="users-table-container">
          {/* <PageHeader title="Socios morosos" /> */}
          <TableContainer component={Paper}>
            <Box
              sx={{
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
              }}
            >
              <TextField
                label="Cant. de cuotas no aprobadas"
                variant="standard"
                type="number"
                value={qxCnaInput}
                onChange={(e) => setQxCnaInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Button
                  variant="contained"
                    // disabled={selectedCount === 0}
                    disabled={true}
                  sx={{ textTransform: 'none', backgroundColor: Colors.RojoMediline }}
                >
                  Dar de baja socios seleccionados ({selectedCount})
                </Button>
                {downloadButton}
              </Box>
            </Box>
            <Table aria-label="delinquent partners table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={rows.length > 0 && selectedRowsOnPage.length === rows.length}
                      indeterminate={selectedRowsOnPage.length > 0 && selectedRowsOnPage.length < rows.length}
                      inputProps={{ 'aria-label': 'Seleccionar todos los socios' }}
                      onChange={handleToggleAllRows}
                    />
                  </TableCell>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.key}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell align="center" colSpan={COLUMNS.length + 1}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" colSpan={COLUMNS.length + 1}>
                      {hasSearched ? 'Sin resultados' : 'Realizá una búsqueda para ver los registros.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow
                      key={getRowKey(row, index)}
                      selected={allRowsSelected
                        ? !excludedRowKeys.includes(getRowKey(row, index))
                        : selectedRowKeys.includes(getRowKey(row, index))}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allRowsSelected
                            ? !excludedRowKeys.includes(getRowKey(row, index))
                            : selectedRowKeys.includes(getRowKey(row, index))}
                          inputProps={{ 'aria-label': `Seleccionar a ${row.nombre ?? 'socio'} ${row.apellido ?? ''}` }}
                          onChange={() => handleToggleRow(row, index)}
                        />
                      </TableCell>
                      {COLUMNS.map((col) => (
                        <TableCell key={col.key}>{formatValue(col.key, row[col.key])}</TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </div>
      </div>
    </ThemeProvider>
  );
}
